import { Trans, useTranslation } from 'react-i18next';
import { faLock } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Alert, Icon, Modal } from '@seeqdev/qomponents';
import type { WorkbookApprovalFlow } from '@/worksheet/worksheet.types';

type ApprovalFlowModalProps = {
  isApplyingFlow?: boolean;
  approvalFlow: WorkbookApprovalFlow | null;
  itemType: string;
  isApplying: boolean;
  onSubmit: () => Promise<void>;
  onClose: () => void;
};

export const ApprovalFlowModal = ({
  isApplyingFlow = false,
  approvalFlow,
  itemType,
  isApplying,
  onSubmit,
  onClose,
}: ApprovalFlowModalProps) => {
  const { t } = useTranslation();
  const orderedSteps = [...(approvalFlow?.steps || [])].sort((left, right) => left.position - right.position);
  const lockStep = orderedSteps.find((step) => step.lockRequired);
  const finalStep = orderedSteps[orderedSteps.length - 1];

  return (
    <Modal
      open={true}
      onClose={onClose}
      onSubmit={onSubmit}
      disableSubmitButton={isApplying}
      submitButtonLabel={isApplyingFlow ? t('APPROVAL_FLOWS.APPLY') : t('DONE')}
      cancelButtonLabel={t('CANCEL')}
      testId="applyApprovalFlowModal"
      title={
        isApplyingFlow
          ? t('APPROVAL_FLOWS.APPLY_TITLE')
          : t('APPROVAL_FLOWS.VIEW_TITLE', { flowName: approvalFlow?.name })
      }>
      <div className="tw:flex tw:flex-col tw:gap-4" aria-busy={isApplying}>
        {isApplying ? (
          <span role="status" className="tw:sr-only">
            {t('APPROVAL_FLOWS.APPLYING')}
          </span>
        ) : null}
        {isApplyingFlow ? (
          <div className="tw:flex tw:flex-col tw:gap-4" data-testid="applyApprovalFlowDescription">
            <p>{t('APPROVAL_FLOWS.APPLY_DESCRIPTION', { flowName: approvalFlow?.name, itemType })}</p>
            {lockStep ? (
              <p>{t('APPROVAL_FLOWS.APPLY_LOCK_DESCRIPTION', { lockStep: lockStep.name, itemType })}</p>
            ) : null}
          </div>
        ) : null}
        <div>
          <div id="approval-steps-label" className="tw:mb-2 tw:font-bold tw:text-inherit">
            {t('APPROVAL_FLOWS.STEPS')}
          </div>
          <ol aria-labelledby="approval-steps-label" className="tw:my-1 tw:list-decimal tw:gap-1 tw:pl-5">
            {orderedSteps.map((step) => (
              <li key={step.id} className="tw:gap-1" data-testid={`applyApprovalFlowStep-${step.id}`}>
                <span>{step.name}</span>
                {step.lockRequired ? (
                  <Icon
                    icon={faLock}
                    extraClassNames="tw:ml-1"
                    testId={`applyApprovalFlowStepLock-${step.id}`}
                    tooltip={t('APPROVAL_FLOWS.LOCK_REQUIRED', { itemType })}
                    aria-label={t('APPROVAL_FLOWS.LOCK_REQUIRED', { itemType })}
                  />
                ) : null}
              </li>
            ))}
          </ol>
        </div>
        {isApplyingFlow && finalStep ? (
          <Alert
            variant="warning"
            dismissible={false}
            testId="applyApprovalFlowProductionImpact"
            extraClassNames="tw:items-start">
            <span className="tw:w-full">
              <Trans
                i18nKey="APPROVAL_FLOWS.APPLY_FINAL_STEP_PRODUCTION_DESCRIPTION"
                values={{
                  stepName: finalStep.name,
                  itemType,
                }}
                components={{ Status: <em /> }}
              />
            </span>
          </Alert>
        ) : null}
      </div>
    </Modal>
  );
};
